// Pure adapter: FilterBuilder condition state -> sqlize-query `where` object for
// GET /payments. Kept free of React/DOM so it is unit-testable in isolation.
//
// Guarantees:
//   • never emits `id` (not in the allow-list — reserved for the keyset cursor)
//   • never emits Postgres-only operators (MySQL backend); $iLike/$notILike are
//     remapped to $like/$notLike, anything else unknown is dropped
//   • drops empty / invalid conditions; returns {} when nothing valid remains
//   • passes relative-date literals (@LAST_24_HOURS, …) through untouched

import { ALLOWED_FIELDS } from './constants';

const ALLOWED = new Set(ALLOWED_FIELDS);

// Postgres-only op -> MySQL-safe equivalent. MySQL's default collation is
// case-insensitive, so $like already covers the $iLike use case.
const OP_REMAP = { $iLike: '$like', $notILike: '$notLike' };

const ALLOWED_OPS = new Set([
  '$eq',
  '$ne',
  '$gt',
  '$gte',
  '$lt',
  '$lte',
  '$is',
  '$like',
  '$notLike',
  '$startsWith',
  '$endsWith',
  '$substring',
  '$in',
  '$notIn',
  '$between',
  '$notBetween',
]);

const RANGE_OPS = new Set(['$between', '$notBetween']);
const ARRAY_OPS = new Set(['$in', '$notIn']);
const NULL_OPS = new Set(['$is']);

function coerce(raw, fieldType) {
  if (fieldType === 'number') {
    if (raw === '' || raw == null) return '';
    const n = Number(raw);
    return Number.isNaN(n) ? '' : n;
  }
  if (fieldType === 'boolean') return raw === true || raw === 'true';
  return raw; // strings + date literals (@…) pass through untouched
}

function isBlank(v) {
  return v == null || String(v).trim() === '';
}

// Build one `{ field: { op: value } }` clause, or null if the condition is
// invalid / empty / targets a disallowed column or operator.
function toClause(cond) {
  if (!cond || !ALLOWED.has(cond.field)) return null;

  const op = OP_REMAP[cond.operator] || cond.operator;
  if (!ALLOWED_OPS.has(op)) return null;

  const { field, fieldType, value } = cond;

  if (NULL_OPS.has(op)) return { [field]: { [op]: null } };

  if (RANGE_OPS.has(op)) {
    const [a, b] = Array.isArray(value) ? value : ['', ''];
    if (isBlank(a) || isBlank(b)) return null;
    const from = coerce(a, fieldType);
    const to = coerce(b, fieldType);
    if (from === '' || to === '') return null;
    return { [field]: { [op]: [from, to] } };
  }

  if (ARRAY_OPS.has(op)) {
    const raw = Array.isArray(value) ? value : String(value).split(',');
    const arr = raw
      .map((v) => String(v).trim())
      .filter((v) => v !== '')
      .map((v) => coerce(v, fieldType))
      .filter((v) => v !== '');
    if (!arr.length) return null;
    return { [field]: { [op]: arr } };
  }

  if (isBlank(value)) return null;
  const scalar = coerce(value, fieldType);
  if (scalar === '') return null;
  return { [field]: { [op]: scalar } };
}

/**
 * FilterBuilder condition list -> sqlize-query `where` object.
 * @param {Array}  conditions - [{ field, fieldType, operator, value }]
 * @param {string} logicalOp  - '$and' (default) | '$or'
 * @returns {Object} where object, or {} when nothing valid remains
 */
export function serializeWhere(conditions, logicalOp) {
  const parts = (conditions || []).map(toClause).filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return parts[0];
  const op = logicalOp === '$or' ? '$or' : '$and';
  return { [op]: parts };
}

/**
 * Serialize + URL-encode into a `where=…` query fragment, or '' when the filter
 * is empty (caller omits the param entirely).
 */
export function toWhereQuery(conditions, logicalOp) {
  const where = serializeWhere(conditions, logicalOp);
  if (Object.keys(where).length === 0) return '';
  return `where=${encodeURIComponent(JSON.stringify(where))}`;
}
